import { NoData } from '@saucelabs/ds-data-states';
import { IconImageOutline } from '@saucelabs/ds-icons';
import { Loader } from '@saucelabs/sl-component-loader';
import type Konva from 'konva';
import { throttle } from 'lodash';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Image as KonvaImage, Layer, Rect, Stage } from 'react-konva';

import { actions, constants, selectors } from '../../ducks/diffUi';
import { DiffUiDisplayType } from '../../ducks/diffUi/constants';
import { constrainZoom } from '../../ducks/diffUi/utils';
import { ImageType } from '../../global/constants';
import { useFragment as getFragment, graphql } from '../../graphql/__generated__';
import {
  DiffStageMetaFragmentFragment,
  DiffStatus,
  RegionFragFragment,
} from '../../graphql/__generated__/graphql';
import { useVisualDispatch, useVisualSelector } from '../../reduxHooks';
import { RectFrag } from './DiffView';
import DiffLabelsTitle from './DiffLabelsTitle';
import { DiffRect, DiffRectType } from './components/DiffRect';
import { StageWrapper } from './components/StageWrapper';
import { ZOOM_MODIFIER_EVENT_PROP, useStageKeybinds } from './useStageKeybinds';
import DiffStageMeta, { DiffStageMetaProps } from './DiffStageMeta';

const data: Record<ImageType, { empty: { title: string, description: string } }> = {
  [ImageType.BASELINE]: { empty: { title: 'No baseline detected', description: 'Accept the current snapshot to create a baseline' } },
  [ImageType.SNAPSHOT]: { empty: { title: 'No current snapshot detected', description: 'Check the configuration of your tests, or if the assets for this test have expired.' } },
};

export const DiffStageMetaFragment = graphql(`
  fragment DiffStageMetaFragment on Diff {
    id
    status
    updatedByUser {
      id
      username
    }
    updatedAt
    baseline {
      id
      createdAt
      branch
      createdByUser {
        id
        username
      }
    }
    snapshot {
      id
      branch
      createdAt
    }
    diffClusters {
      ...RectFrag
    }
  }
`);

export const DiffStage: FC<DiffStageProps> = (props) => {
  const { url, ignoreRegions, type, diff } = props;
  const [image, setImage] = useState<HTMLImageElement>();
  const { baseline, diffClusters } = diff;

  const stageRef = useRef<Konva.Stage>(null);
  const {
    syncPan,
    offset,
    zoom,
    diffSettings,
    stageDims,
    singleDisplayType,
    displayType,
  } = useVisualSelector(selectors.getDiffUi);
  const { width, height } = stageDims;
  const dispatch = useVisualDispatch();

  const [localZoom, setLocalZoom] = useState(1);
  const [localOffset, setLocalOffset] = useState({
    x: 0,
    y: 0,
  });

  useEffect(() => {
    if (url) {
      const img = new Image();
      img.onload = function (this: HTMLImageElement) {
        setImage(this);
      };
      img.src = url;
    }
  }, [url]);

  useEffect(() => {
    if (image) {
      dispatch(actions.setImageDims({
        type,
        dims: {
          height: image.height,
          width: image.width,
        },
      }));
      dispatch(actions.zoomToFit());
    }
  }, [type, image, dispatch]);

  /**
   * Blurs the active element. Used below to remove focus on an element after hovering over
   * the stage, so we do not accidentally press / interact with the previously focused element.
   */
  const blurActive = useCallback(() => {
    const { activeElement } = document;
    if (activeElement && 'blur' in activeElement) {
      (activeElement as HTMLElement).blur();
    }
  }, []);

  const {
    isDragging,
    setIsDragging,
    setIsHoveringShape,
    zoomDirectionRef,
    stageStyles,
  } = useStageKeybinds();

  const updateOffset = useCallback(throttle((event: Konva.KonvaEventObject<DragEvent>) => {
    const { target } = event;
    const newOffset = {
      x: target.x(),
      y: target.y(),
    };

    if (syncPan) {
      dispatch(actions.setOffset(newOffset));
    }

    setLocalOffset(newOffset);
  }, 10), [dispatch, syncPan]);

  const updateZoom = useCallback(throttle((event: Konva.KonvaEventObject<WheelEvent>) => {
    const { evt } = event;
    const stage = stageRef.current;

    if (!stage || !event.evt[ZOOM_MODIFIER_EVENT_PROP]) {
      return;
    }

    evt.preventDefault();

    const currentScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const { x: pointerX = 0, y: pointerY = 0 } = pointer ?? {};

    const mousePointTo = {
      x: (pointerX - stage.x()) / currentScale,
      y: (pointerY - stage.y()) / currentScale,
    };

    const direction = evt.deltaY > 0 ? -1 : 1;
    zoomDirectionRef.current = direction;

    const newScale = constrainZoom(
      direction > 0 ? (
        currentScale * constants.SCROLL_ZOOM_SCALE
      ) : currentScale / constants.SCROLL_ZOOM_SCALE,
    );

    // Return early to prevent unnecessary renders if scale isn't changing on zoom (max / min met)
    if (currentScale === newScale) {
      return;
    }

    const newOffset = {
      x: pointerX - mousePointTo.x * newScale,
      y: pointerY - mousePointTo.y * newScale,
    };

    if (syncPan) {
      dispatch(actions.setZoom(newScale));
      dispatch(actions.setOffset(newOffset));
    }

    setLocalZoom(newScale);
    setLocalOffset(newOffset);
  }, 10), [dispatch, syncPan]);

  // Sync local offset & zoom with state from store for / if / when it's toggled
  useEffect(() => {
    if (!syncPan) {
      setLocalOffset(offset);
    }
  }, [syncPan, offset]);
  useEffect(() => {
    if (!syncPan) {
      setLocalZoom(zoom);
    }
  }, [syncPan, zoom]);

  const clusters = diffClusters
    .map((cluster) => getFragment(RectFrag, cluster!));

  // If we're in single display mode, only render the primary display type. This lets us have a
  // single DOM element in the stage, but keep / share all the state, so we can quickly toggle
  // between view types.
  if (displayType === DiffUiDisplayType.Single && singleDisplayType !== type) {
    return null;
  }

  // If there isn't a URL available for this image type then display our nodata component centered
  // in the screen.
  if (!url) {
    return (
      <StageWrapper>
        <NoData
          description={data[ImageType.BASELINE].empty.description}
          icon={IconImageOutline}
          spacing="small"
          title={data[ImageType.BASELINE].empty.title}
        />
      </StageWrapper>
    );
  }

  if (!image) {
    return (
      <StageWrapper>
        <Loader />
      </StageWrapper>
    );
  }

  // Only display rect overlays on snapshot, unless we're hiding & emphasizing diffs at the same
  // time to make it easier to compare.
  const shouldDisplayRects = (
    type === ImageType.SNAPSHOT ||
    (
      diffSettings.emphasizeDiffs && diffSettings.hideDiffs
    )
  );

  const metaProps: Record<ImageType, DiffStageMetaProps> = {
    [ImageType.BASELINE]: {
      status: DiffStatus.Approved,
      updatedAt: baseline?.createdAt,
      updatedBy: baseline?.createdByUser.username ?? undefined,
      branch: baseline?.branch ?? undefined,
    },
    [ImageType.SNAPSHOT]: {
      status: diff.status,
      updatedAt: diff.updatedAt,
      updatedBy: diff.updatedByUser?.username ?? undefined,
      branch: diff.snapshot?.branch ?? undefined,
      changesCounter: clusters.length,
    },
  };

  return (
    <StageWrapper>
      <DiffLabelsTitle
        side={type}
      >
        <DiffStageMeta
          {...metaProps[type]}
        />
      </DiffLabelsTitle>
      <Stage
        ref={stageRef}
        draggable
        height={height || 1}
        onDragEnd={() => setIsDragging(false)}
        onDragMove={updateOffset}
        onDragStart={() => setIsDragging(true)}
        onMouseEnter={blurActive}
        onWheel={updateZoom}
        scaleX={syncPan ? zoom : localZoom}
        scaleY={syncPan ? zoom : localZoom}
        style={stageStyles}
        width={width || 1}
        x={syncPan && !isDragging ? offset.x : localOffset.x}
        y={syncPan && !isDragging ? offset.y : localOffset.y}
      >
        <Layer>
          <KonvaImage
            cornerRadius={2}
            fill="white"
            height={image.height || 1}
            image={image}
            stroke="#E8EBF1"
            strokeWidth={2 / zoom}
            width={image.width || 1}
          />
        </Layer>
        {shouldDisplayRects && (
          <Layer>
            {diffSettings.emphasizeDiffs && (
              <Rect
                fill="white"
                height={image.height}
                opacity={0.8}
                width={image.width}
              />
            )}
            {ignoreRegions?.map((rect, index) => (
              <DiffRect
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                localZoom={localZoom}
                rect={rect}
                setIsHoveringShape={setIsHoveringShape}
                type={DiffRectType.IGNORED}
              />
            ))}
            {clusters.map((rect, index) => (
              <DiffRect
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                localZoom={localZoom}
                rect={rect}
                setIsHoveringShape={setIsHoveringShape}
                type={DiffRectType.CHANGED}
              />
            ))}
          </Layer>
        )}
      </Stage>
    </StageWrapper>
  );
};

export interface DiffStageProps {
  diff: DiffStageMetaFragmentFragment;
  type: ImageType;
  url: string | undefined;
  ignoreRegions?: RegionFragFragment[];
}
